/**
 * Extracts donation details from the abandoned gift data.
 * @param {object} abandonedGift
 * @param {object} abandonedGift.storedAbandonedGiftData
 * @param {object} abandonedGift.storedAbandonedGiftData.donationDetails
 * @returns {object} The donation details from the abandoned gift data.
 */
export const getDonationDetailsFromAbandonedGiftData = (abandonedGift) => {
    const { storedAbandonedGiftData = {} } = abandonedGift;
    const { donationDetails = {} } = storedAbandonedGiftData;
    return donationDetails;
};

/**
 * Determines the value to insert into formik initial values for gift details
 * @param {string} key - The key of the gift detail.
 * @param {string|number|boolean} value - The value of the gift detail.
 * @returns {string|number|boolean} The value to use for the gift detail.
 */
export const getGiftDetailsValueToUse = (key, value) => {
    if (typeof key === "string" && key.includes("Multi_Restriction")) {
        // each value here is an object that needs to be properly sanitized
        // for other amounts
        const multiRestrictionValuesObjectKeys = Object.keys(value);
        const sanitizedMultiRestrictionValue =
            multiRestrictionValuesObjectKeys.reduce(
                (totalMultiRestrictionValue, currentMultiRestrictionKey) => {
                    const valueToUse = getGiftDetailsValueToUse(
                        currentMultiRestrictionKey,
                        value[currentMultiRestrictionKey],
                    );

                    return {
                        ...totalMultiRestrictionValue,
                        [currentMultiRestrictionKey]: valueToUse,
                    };
                },
                {},
            );
        return sanitizedMultiRestrictionValue;
    }
    if (key === "Other_One_Time_Amount" || key === "Other_Recurring_Amount") {
        return `${value}`;
    }
    return value;
};

/**
 * Determines the value to insert into formik initial values for donor details
 * @param {string} key - The key of the gift detail.
 * @param {string|number|boolean} value - The value of the gift detail.
 * @returns {string|number|boolean} The value to use for the gift detail.
 */
export const getDonorDetailsValueToUse = (key, value) => {
    if (key === "Zip" || key === "Phone") {
        return `${value}`;
    }
    return value;
};

/**
 * Overrides initial values from settings with values from abandoned gifts data.
 * @param {object} initialValuesFromSettings - The formik initial values from settings.
 * @param {object} valuesFromAbandonedGifts - The values from abandoned gifts stored data.
 * @param {Function} valueSanitizeFunction - The function to sanitize specific values to strings.
 * @returns {object} The initial values for formik
 */
export const abandonedGiftsDataInitialValuesOverrideReducer = (
    initialValuesFromSettings,
    valuesFromAbandonedGifts,
    valueSanitizeFunction,
) => {
    const initialValuesFromSettingsKeys = Object.keys(
        initialValuesFromSettings,
    );
    const valuesFromAbandonedGiftsKeys = Object.keys(valuesFromAbandonedGifts);

    const initialValuesWithAbandonedGiftsData =
        initialValuesFromSettingsKeys.reduce(
            (totalInitialValues, currentInitialValueKey) => {
                const hasAbandonedGiftsRelatedValue =
                    valuesFromAbandonedGiftsKeys.includes(
                        currentInitialValueKey,
                    );
                if (!hasAbandonedGiftsRelatedValue) {
                    return {
                        ...totalInitialValues,
                        [currentInitialValueKey]:
                            initialValuesFromSettings[currentInitialValueKey],
                    };
                }
                const valueToUse = valueSanitizeFunction(
                    currentInitialValueKey,
                    valuesFromAbandonedGifts[currentInitialValueKey],
                );

                return {
                    ...totalInitialValues,
                    [currentInitialValueKey]: valueToUse,
                };
            },
            {},
        );

    return initialValuesWithAbandonedGiftsData;
};

/**
 * @function getAbandonedGiftGiftDetailsInitialValues
 * @param {object} giftDetailsFromAbandonedGiftData - The formik initial values from settings.
 * @param {object} existingGiftDetailsInitialValuesFromSettings - The values from abandoned gifts stored data.
 * @returns {object} gift details initial values from abandoned gifts
 */
export const getAbandonedGiftGiftDetailsInitialValues = (
    giftDetailsFromAbandonedGiftData,
    existingGiftDetailsInitialValuesFromSettings,
) => {
    const giftDetailsWithAbandonedGiftData =
        abandonedGiftsDataInitialValuesOverrideReducer(
            existingGiftDetailsInitialValuesFromSettings,
            giftDetailsFromAbandonedGiftData,
            getGiftDetailsValueToUse,
        );
    return giftDetailsWithAbandonedGiftData;
};

/**
 * @function getAbandonedGiftDonorDetailsInitialValues
 * @param {object} params
 * @param {object} params.initialValuesFromSettings - The formik initial values from settings.
 * @param {object} params.storedAbandonedGiftData - The values from abandoned gifts stored data.
 * @param {object} params.savedValues - The values from redux for saved donor details
 * @returns {object} donor details initial values from abandoned gifts
 */
export const getAbandonedGiftDonorDetailsInitialValues = ({
    initialValuesFromSettings,
    storedAbandonedGiftData,
    savedValues,
}) => {
    const { donationDetails = {}, allowedFormikValues = {} } =
        storedAbandonedGiftData;
    const { donorDetails = {} } = donationDetails;

    const donorDetailsWithAllowedFormikValues = {
        ...donorDetails,
        ...allowedFormikValues,
    };

    const donorDetailsWithValidAbandonedGiftsData =
        abandonedGiftsDataInitialValuesOverrideReducer(
            initialValuesFromSettings,
            donorDetailsWithAllowedFormikValues,
            getDonorDetailsValueToUse,
        );
    // we can trust saved values since they would only have values
    // for fields that came from the current instance of the form
    return { ...donorDetailsWithValidAbandonedGiftsData, ...savedValues };
};
